// ============================================================
// View: Zeitverlauf — Einsatz/Auszahlung, Kumuliert, Divergierend
// ============================================================

import { useMemo, useState } from 'react';
import { Card, CardTitle, EurCell, NetCell } from '../components/ui';
import { CumulativeChart, DivergingChart, Legend, TwoSeriesBars } from '../components/charts';
import { CO } from '../theme';
import { useApp } from '../store';
import { groupRows, inRange, monthlyRows, period } from '../aggregator';
import { eur } from '../format';
import type { GroupMode } from '../types';

const GROUP_LABELS: Record<GroupMode, string> = {
  tag: 'Tag',
  woche: 'Woche',
  monat: 'Monat',
  jahr: 'Jahr',
};

const GROUP_GROUP_LABELS: Record<GroupMode, string> = {
  tag: 'Tag',
  woche: 'Woche',
  monat: 'Monat',
  jahr: 'Jahr',
};

export function Zeitverlauf() {
  const { history, periodMode, customFrom, customTo } = useApp();
  const work = history?.eintraege ?? [];
  const [ztGroup, setZtGroup] = useState<GroupMode>('monat');

  const p = useMemo(() => period(periodMode, work, customFrom, customTo), [periodMode, work, customFrom, customTo]);
  const filtered = useMemo(() => (p.full ? work.slice() : work.filter((e) => inRange(e, p))), [work, p]);
  const rows = useMemo(() => groupRows(filtered, ztGroup), [filtered, ztGroup]);
  const months = useMemo(() => monthlyRows(filtered), [filtered]);

  const segBtn = (
    k: GroupMode,
    l: string,
  ) => (
    <button
      key={k}
      onClick={() => setZtGroup(k)}
      style={{
        border: 'none',
        background: ztGroup === k ? '#fff' : 'transparent',
        color: ztGroup === k ? CO.text : CO.muted,
        padding: '6px 14px',
        borderRadius: 7,
        fontSize: 12.5,
        fontWeight: 600,
        boxShadow: ztGroup === k ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
        cursor: 'pointer',
      }}
    >
      {l}
    </button>
  );

  return (
    <div>
      <Card>
        <CardTitle
          title="Einsatz & Auszahlung im Zeitverlauf"
          sub={`${p.label} · Werte in Euro`}
          right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Legend items={[{ l: 'Einsatz', c: CO.einsatz }, { l: 'Auszahlung', c: CO.auszahlung }]} />
              <div
                style={{
                  display: 'inline-flex',
                  background: '#f0f2ed',
                  borderRadius: 9,
                  padding: 3,
                }}
              >
                {(['tag', 'woche', 'monat', 'jahr'] as GroupMode[]).map((k) => segBtn(k, GROUP_LABELS[k]))}
              </div>
            </div>
          }
        />
        <div style={{ overflowX: 'auto' }}>
          <TwoSeriesBars rows={rows} colorEinsatz={CO.einsatz} colorAuszahlung={CO.auszahlung} />
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
        <Card>
          <CardTitle
            title="Kumuliertes Nettoergebnis"
            sub={`${p.label} · laufende Summe aller Nettoergebnisse in Euro`}
          />
          <div style={{ overflowX: 'auto' }}>
            <CumulativeChart entries={filtered} />
          </div>
        </Card>
        <Card>
          <CardTitle
            title="Monatliche Nettobilanz"
            sub={`${p.label} · positive und negative Monatswerte um die Null-Linie`}
          />
          <div style={{ overflowX: 'auto' }}>
            <DivergingChart rows={months.map((m) => ({ label: m.label, netto: m.netto }))} />
          </div>
        </Card>
      </div>

      <Card style={{ marginTop: 20 }}>
        <CardTitle
          title="Aktivität"
          sub={`${p.label} · Einträge und durchschnittlicher Einsatz je ${GROUP_GROUP_LABELS[ztGroup]}`}
        />
        {rows.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ color: CO.faint }}>
                {['Zeitraum', 'Einträge', 'Einsatz', 'Auszahlung', 'Nettoergebnis', 'Ø Einsatz/Eintrag'].map((c, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: i ? 'right' : 'left',
                      fontWeight: 500,
                      padding: '0 8px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${CO.border2}` }}>
                  <td style={{ padding: '8px' }}>{r.label}</td>
                  <td className="num" style={{ textAlign: 'right', padding: '8px', color: CO.muted }}>
                    {r.n}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    <EurCell value={r.einsatz} />
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    <EurCell value={r.gewinn} tone="auszahlung" />
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px' }}>
                    <NetCell n={r.netto} />
                  </td>
                  <td className="num" style={{ textAlign: 'right', padding: '8px', color: CO.muted }}>
                    {eur(r.n ? r.einsatz / r.n : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: CO.faint }}>Keine Daten.</div>
        )}
      </Card>
    </div>
  );
}
